import { api } from "./api";
import { getToken } from "./auth";
import type {
  Advertiser,
  Client,
  MediaItem,
  Playlist,
  PlaylistAssignment,
  ReportFilters,
  Screen,
  ScreenStats,
  ScreensResponse,
  Settings,
  User,
} from "../types/dashboard";

// ---------- auth/me ----------
export async function getMe(): Promise<User> {
  const { data } = await api.get<User>("/api/auth/me");
  return data;
}

export async function changePassword(currentPassword: string, newPassword: string) {
  await api.post("/api/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

// ---------- screens ----------
export interface ScreenQuery {
  page: number;
  per_page?: number;
  status?: string;
  claim?: string;
  client_id?: number | string;
  search?: string;
}

export async function listScreens(q: ScreenQuery): Promise<ScreensResponse> {
  const params = new URLSearchParams();
  params.set("page", String(q.page));
  params.set("per_page", String(q.per_page ?? 50));
  if (q.status) params.set("status", q.status);
  if (q.claim) params.set("claim", q.claim);
  if (q.client_id) params.set("client_id", String(q.client_id));
  if (q.search) params.set("search", q.search);
  const { data } = await api.get<ScreensResponse>(`/api/screens?${params.toString()}`);
  return data;
}

export async function getScreenStats(): Promise<ScreenStats> {
  const { data } = await api.get<ScreenStats>("/api/screens/stats");
  return data;
}

export async function updateScreen(id: number, body: Partial<Screen>): Promise<Screen> {
  // Backend exposes PUT /screens/{id} (no PATCH route).
  const { data } = await api.put<Screen>(`/api/screens/${id}`, body);
  return data;
}

export async function deleteScreen(id: number): Promise<void> {
  await api.delete(`/api/screens/${id}`);
}

export async function claimScreen(id: number): Promise<void> {
  // /claim is the public Pi-side route (takes a claim code in body).
  // /claim-dashboard is the dashboard-side claim used after generate-claim-code.
  await api.post(`/api/screens/${id}/claim-dashboard`);
}

export async function rebootScreen(id: number): Promise<void> {
  // Pushed to the Pi over its live WebSocket; 409 if the player is offline.
  await api.post(`/api/screens/${id}/reboot`);
}

export async function generateClaimCode(id: number): Promise<{ code: string }> {
  const { data } = await api.post<{ code: string }>(
    `/api/screens/${id}/generate-claim-code`,
  );
  return data;
}

export async function getScreenAssignments(id: number): Promise<PlaylistAssignment[]> {
  const { data } = await api.get<PlaylistAssignment[]>(`/api/screens/${id}/playlists`);
  return data;
}

export async function setScreenAssignments(
  id: number,
  assignments: PlaylistAssignment[],
): Promise<void> {
  await api.put(`/api/screens/${id}/playlists`, { assignments });
}

// ---------- clients (screen groups) ----------
export async function listClients(): Promise<Client[]> {
  // Backend wraps the list in a pagination envelope (clients/total/page/...);
  // we only need the array here. Same pattern as listPlaylists, listMedia.
  const { data } = await api.get<{ clients: Client[] }>("/api/clients");
  return data.clients;
}

export async function getClient(id: number): Promise<Client> {
  const { data } = await api.get<Client>(`/api/clients/${id}`);
  return data;
}

export async function saveClient(body: Partial<Client> & { id?: number }): Promise<Client> {
  if (body.id) {
    // Backend exposes PUT /clients/{id} (no PATCH route).
    const { data } = await api.put<Client>(`/api/clients/${body.id}`, body);
    return data;
  }
  const { data } = await api.post<Client>("/api/clients", body);
  return data;
}

export async function deleteClient(id: number): Promise<void> {
  await api.delete(`/api/clients/${id}`);
}

export async function getClientAssignments(id: number): Promise<PlaylistAssignment[]> {
  const { data } = await api.get<PlaylistAssignment[]>(`/api/clients/${id}/playlists`);
  return data;
}

export async function setClientAssignments(
  id: number,
  assignments: PlaylistAssignment[],
): Promise<void> {
  await api.put(`/api/clients/${id}/playlists`, { assignments });
}

// ---------- advertisers ----------
export async function listAdvertisers(): Promise<Advertiser[]> {
  const { data } = await api.get<Advertiser[]>("/api/advertisers");
  return data;
}

export async function saveAdvertiser(
  body: Partial<Advertiser> & { id?: number },
): Promise<Advertiser> {
  if (body.id) {
    // Backend exposes PUT /advertisers/{id} (no PATCH route).
    const { data } = await api.put<Advertiser>(`/api/advertisers/${body.id}`, body);
    return data;
  }
  const { data } = await api.post<Advertiser>("/api/advertisers", body);
  return data;
}

export async function deleteAdvertiser(id: number): Promise<void> {
  await api.delete(`/api/advertisers/${id}`);
}

export async function getAdvertiserMedia(id: number): Promise<MediaItem[]> {
  const { data } = await api.get<MediaItem[]>(`/api/advertisers/${id}/media`);
  // Backend's MediaOut has no `url` (only `filename`); compose it like
  // listMedia does, otherwise <img src> is empty and thumbnails render grey.
  return data.map(withMediaUrl);
}

export async function attachMediaToAdvertiser(id: number, mediaIds: number[]): Promise<void> {
  await api.post(`/api/advertisers/${id}/media`, { media_ids: mediaIds });
}

export async function detachMediaFromAdvertiser(id: number, mediaId: number): Promise<void> {
  await api.delete(`/api/advertisers/${id}/media/${mediaId}`);
}

// Advertiser ↔ Playlist is an organizational many-to-many. The list endpoint
// returns playlist summaries (no `items`); reuse fromBackendPlaylist so the
// composed `thumbnail_url` and renamed fields match the rest of the UI.
export async function getAdvertiserPlaylists(id: number): Promise<Playlist[]> {
  const { data } = await api.get<BackendPlaylist[]>(`/api/advertisers/${id}/playlists`);
  return data.map(fromBackendPlaylist);
}

export async function attachPlaylistsToAdvertiser(
  id: number,
  playlistIds: number[],
): Promise<void> {
  await api.post(`/api/advertisers/${id}/playlists`, { playlist_ids: playlistIds });
}

export async function detachPlaylistFromAdvertiser(
  id: number,
  playlistId: number,
): Promise<void> {
  await api.delete(`/api/advertisers/${id}/playlists/${playlistId}`);
}

// Resolve a playlist's items into full MediaItems (with composed urls) for the
// image preview gallery. The detail endpoint embeds `media` per item; flatten
// it so the lightbox can render straight from the result.
export async function getPlaylistMedia(id: number): Promise<MediaItem[]> {
  const { data } = await api.get<{
    items?: Array<{ media: MediaItem }>;
  }>(`/api/playlists/${id}`);
  return (data.items ?? []).map((it) => withMediaUrl(it.media));
}

// ---------- media ----------
export interface MediaQuery {
  search?: string;
  advertiser?: string;
  uploader?: string;
  type?: string;
  sort?: string;
}

// Backend's MediaOut returns `filename` but no `url` (the Pi player and
// other consumers derive the URL from filename). Compose it here so the
// dashboard's <img src={m.url}> just works.
function withMediaUrl(m: MediaItem): MediaItem {
  return { ...m, url: `/media/${m.filename}` };
}

export async function listMedia(q: MediaQuery = {}): Promise<MediaItem[]> {
  const params = new URLSearchParams();
  if (q.search) params.set("search", q.search);
  if (q.advertiser) params.set("advertiser", q.advertiser);
  if (q.uploader) params.set("uploader", q.uploader);
  if (q.type) params.set("type", q.type);
  if (q.sort) params.set("sort", q.sort);
  const { data } = await api.get<{ media: MediaItem[] }>(`/api/media?${params.toString()}`);
  return data.media.map(withMediaUrl);
}

export interface UploadProgress {
  /** 0-based index of the file currently uploading */
  fileIndex: number;
  fileCount: number;
  fileName: string;
  /** overall progress across ALL selected files, byte-weighted, 0-100 */
  percent: number;
}

// Transport split: browsers use XMLHttpRequest because fetch cannot observe
// upload progress at all. But under vitest, jsdom's XHR + MSW hang forever on
// Node 20 whenever the body is FormData containing a *file part* (string
// fields and raw Blob bodies intercept fine — isolated empirically; axios hits
// a similar wall, see git history). So MSW-backed tests run the fetch
// transport, and the XHR transport is unit-tested directly with a stubbed
// XMLHttpRequest (uploadFileXhr.test.ts) so both paths stay covered.
const USE_FETCH_TRANSPORT = import.meta.env.MODE === "test";

export async function uploadMedia(
  files: File[],
  advertiserId?: number,
  onProgress?: (p: UploadProgress) => void,
): Promise<MediaItem[]> {
  // Backend accepts one file per request (`file: UploadFile`, not a list).
  // Loop and upload sequentially so multi-select still works from the UI.
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1;
  let doneBytes = 0;
  const uploaded: MediaItem[] = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const report = (loadedOfCurrent: number) =>
      onProgress?.({
        fileIndex: i,
        fileCount: files.length,
        fileName: f.name,
        percent: Math.min(100, Math.round(((doneBytes + loadedOfCurrent) / totalBytes) * 100)),
      });
    report(0);
    const data = USE_FETCH_TRANSPORT
      ? await uploadFileFetch(f, advertiserId)
      : await uploadFileXhr(f, advertiserId, report);
    doneBytes += f.size;
    report(f.size);
    uploaded.push(withMediaUrl(data));
  }
  return uploaded;
}

function makeUploadForm(file: File, advertiserId: number | undefined): FormData {
  const form = new FormData();
  form.append("file", file);
  if (advertiserId) form.append("advertiser_id", String(advertiserId));
  return form;
}

/** Exported for direct unit testing with a stubbed XMLHttpRequest. */
export function uploadFileXhr(
  file: File,
  advertiserId: number | undefined,
  onLoaded: (loadedBytes: number) => void,
): Promise<MediaItem> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/media");
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onLoaded(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as MediaItem);
        } catch {
          reject(new Error("Upload failed: invalid server response"));
        }
        return;
      }
      let msg = `Upload failed: HTTP ${xhr.status}`;
      try {
        const detail = (JSON.parse(xhr.responseText) as { detail?: unknown }).detail;
        if (typeof detail === "string") msg = detail;
      } catch {
        // non-JSON error body — keep the HTTP status message
      }
      reject(new Error(msg));
    };
    xhr.onerror = () => reject(new Error("Upload failed: network error"));
    xhr.send(makeUploadForm(file, advertiserId));
  });
}

async function uploadFileFetch(
  file: File,
  advertiserId: number | undefined,
): Promise<MediaItem> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch("/api/media", {
    method: "POST",
    headers,
    body: makeUploadForm(file, advertiserId),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { detail?: string | Record<string, unknown> }
      | null;
    const detail = body?.detail;
    const msg = typeof detail === "string" ? detail : `Upload failed: HTTP ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as MediaItem;
}

export async function deleteMedia(id: number): Promise<void> {
  await api.delete(`/api/media/${id}`);
}

// ---------- playlists ----------
// Backend returns `thumbnail` (filename of the first item, or null) and
// `thumbnail_type` ("image" | "video" | null). Compose the URL here so
// the dashboard card render doesn't have to repeat the logic.
//
// Also rename backend → frontend on items and campaign_name (the inverse
// of toBackendPlaylist), so the rest of the React code can keep using
// `it.order` and `p.campaign` without scattered field-mapping logic.
// Backend wire shape (PlaylistOut / PlaylistSummary). Kept loose because
// summary rows omit `items` entirely and we tolerate either field name.
type BackendPlaylist = Omit<Playlist, "items" | "campaign"> & {
  campaign?: string | null;
  campaign_name?: string | null;
  items?: Array<{ media_id: number; duration: number; position?: number; order?: number }>;
};

function fromBackendPlaylist(p: BackendPlaylist): Playlist {
  const items = Array.isArray(p.items)
    ? p.items.map((it) => ({
        media_id: it.media_id,
        duration: it.duration,
        order: it.position ?? it.order ?? 0,
      }))
    : [];
  const out: Playlist = {
    ...p,
    items,
    campaign: p.campaign ?? p.campaign_name ?? null,
  };
  if (p.thumbnail) out.thumbnail_url = `/media/${p.thumbnail}`;
  return out;
}

export async function listPlaylists(): Promise<Playlist[]> {
  const { data } = await api.get<{ playlists: BackendPlaylist[] }>("/api/playlists");
  return data.playlists.map(fromBackendPlaylist);
}

// Backend's list endpoint returns PlaylistSummary (no `items` field — only
// `item_count`). The detail endpoint returns PlaylistOut with the actual
// items + their positions/durations. The edit modal needs the latter, so
// fetch it on demand when the user clicks Edit.
export async function getPlaylist(id: number): Promise<Playlist> {
  const { data } = await api.get<BackendPlaylist>(`/api/playlists/${id}`);
  return fromBackendPlaylist(data);
}

// The frontend models use `order` and `campaign` because that's how the React
// code refers to them, but the FastAPI schemas (PlaylistCreate / PlaylistItemIn)
// require `position` and `campaign_name`. Translate on the way out so the
// names match what the backend's Pydantic models accept — otherwise updates
// silently drop campaign_name and persist every item with position=0.
function toBackendPlaylist(body: Partial<Playlist> & { id?: number }) {
  const {
    campaign, items, thumbnail, thumbnail_type, thumbnail_url,
    total_duration, item_count, id, ...rest
  } = body;
  void thumbnail; void thumbnail_type; void thumbnail_url;
  void total_duration; void item_count; void id;
  return {
    ...rest,
    ...(campaign !== undefined ? { campaign_name: campaign } : {}),
    ...(items
      ? {
          items: items.map((it) => ({
            media_id: it.media_id,
            duration: it.duration,
            position: it.order,
          })),
        }
      : {}),
  };
}

export async function savePlaylist(
  body: Partial<Playlist> & { id?: number },
): Promise<Playlist> {
  const payload = toBackendPlaylist(body);
  if (body.id) {
    // Backend exposes PUT /playlists/{id} (full replace, including items);
    // there is no PATCH route, so axios.patch returns 405 here.
    const { data } = await api.put<Playlist>(`/api/playlists/${body.id}`, payload);
    return data;
  }
  const { data } = await api.post<Playlist>("/api/playlists", payload);
  return data;
}

export async function deletePlaylist(id: number): Promise<void> {
  await api.delete(`/api/playlists/${id}`);
}

// ---------- settings ----------
// The backend stores every setting as a string (its schema is dict[str, str]),
// so GET returns strings and PUT must send strings. These two lists let us
// round-trip the typed boolean/number fields cleanly.
const BOOLEAN_SETTINGS = [
  "alert_on_error",
  "alert_on_back_online",
  "alert_digest",
  "alert_enabled",
  "smtp_use_tls",
] as const;
const NUMBER_SETTINGS = [
  "session_timeout_min",
  "alert_offline_hours",
  "smtp_port",
  "purge_logs_days",
  "purge_media_days",
] as const;

export async function getSettings(): Promise<Settings> {
  const { data } = await api.get<Record<string, string>>("/api/settings");
  const out: Record<string, unknown> = { ...data };
  // Coerce strings back to the bool/number types the form expects — without
  // this, `!!"false"` is truthy and every checkbox loads as checked.
  for (const k of BOOLEAN_SETTINGS) if (k in data) out[k] = data[k] === "true";
  for (const k of NUMBER_SETTINGS) if (k in data) out[k] = Number(data[k]);
  return out as Settings;
}

export async function saveSettings(body: Partial<Settings>): Promise<{ ok: boolean }> {
  // Backend exposes PUT /settings (no PATCH route) and expects
  // { settings: { [key]: string } } — its schema is dict[str, str], so every
  // value must be stringified (booleans become "true"/"false").
  const settings: Record<string, string> = {};
  for (const [key, value] of Object.entries(body)) {
    if (value === undefined || value === null) continue;
    settings[key] = String(value);
  }
  const { data } = await api.put<{ ok: boolean }>("/api/settings", { settings });
  return data;
}

export async function testSmtp(recipient: string): Promise<{ ok: boolean; message?: string }> {
  // Backend route is /settings/smtp/test (not /settings/test-smtp) and
  // requires a `recipient` (SmtpTestRequest) — the address the test email
  // is sent to.
  const { data } = await api.post<{ ok: boolean; message?: string }>(
    "/api/settings/smtp/test",
    { recipient },
  );
  return data;
}

// ---------- users (list/invite need admin, the rest is owner only) ----------
export async function listUsers(): Promise<User[]> {
  const { data } = await api.get<User[]>("/api/users");
  return data;
}

export interface InviteResult {
  ok: boolean;
  email_sent: boolean;
  email_error?: string | null;
}

export async function inviteUser(
  email: string,
  role: string,
  name = "",
): Promise<InviteResult> {
  const { data } = await api.post<InviteResult>("/api/users/invite", { email, role, name });
  return data;
}

export async function resendInvite(id: number): Promise<InviteResult> {
  const { data } = await api.post<InviteResult>(`/api/users/${id}/resend-invite`);
  return data;
}

export async function approveUser(id: number, role: string): Promise<void> {
  await api.post(`/api/users/${id}/approve`, { role });
}

export async function denyUser(id: number): Promise<void> {
  await api.post(`/api/users/${id}/deny`);
}

export async function updateUserRole(id: number, role: string): Promise<void> {
  // Backend route is PUT /users/{id}/role (the bare /users/{id} doesn't exist).
  await api.put(`/api/users/${id}/role`, { role });
}

export async function deleteUser(id: number): Promise<void> {
  await api.delete(`/api/users/${id}`);
}

// ---------- reports ----------
// Backend exposes POST /api/reports/generate accepting a JSON body
// (ReportParams). The previous GET /api/reports/pdf?... route never
// existed and 404'd in production. `range_type` is a UI-only field
// (drives the date pickers); the backend only cares about the resolved
// date_from/date_to. Empty-string IDs from unselected dropdowns must be
// dropped, not sent — Pydantic rejects "" for `int | None`.
export async function generateReport(filters: ReportFilters): Promise<Blob> {
  const body: Record<string, unknown> = {
    date_from: filters.date_from,
    date_to: filters.date_to,
    breakdown_daily: !!filters.breakdown_daily,
    breakdown_screen: !!filters.breakdown_screen,
    breakdown_location: !!filters.breakdown_location,
  };
  if (filters.client_id !== undefined && filters.client_id !== "") {
    body.client_id = Number(filters.client_id);
  }
  if (filters.playlist_id !== undefined && filters.playlist_id !== "") {
    body.playlist_id = Number(filters.playlist_id);
  }
  if (filters.screen_id !== undefined && filters.screen_id !== "") {
    body.screen_id = Number(filters.screen_id);
  }
  const res = await api.post("/api/reports/generate", body, { responseType: "blob" });
  return res.data;
}

// ---------- address autocomplete (Photon) ----------
export interface PhotonResult {
  display: string;
  street?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export async function searchAddress(query: string): Promise<PhotonResult[]> {
  if (!query || query.length < 3) return [];
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.features ?? []).map((f: { properties: Record<string, string> }) => {
    const p = f.properties;
    const street = [p.housenumber, p.street].filter(Boolean).join(" ");
    const parts = [street, p.city, p.state, p.postcode, p.country].filter(Boolean);
    return {
      display: parts.join(", "),
      street,
      city: p.city,
      state: p.state,
      postcode: p.postcode,
      country: p.country,
    } as PhotonResult;
  });
}
